import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import FlightDetails from "./FlightResults/FlightDetails";
import {
  FaPlane,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaChair,
  FaSuitcase,
  FaSpinner,
  FaInfoCircle,
  FaUtensils,
  FaExchangeAlt,
  FaCheckCircle,
  FaWeightHanging,
  FaHamburger,
  FaTag,
  FaIdCard,
  FaFileAlt,
} from "react-icons/fa";
import {
  processFareOptions,
  calculateTotalDuration,
  calculateStopsText,
} from "../utils/flightHelpers";
import AirlineLogo from "./FlightResults/AirlineLogo";
import { useCurrency } from "../context/CurrencyContext";
import { formatPriceSync } from "../utils/currency";

const OneWayFlightCard = ({
  flight,
  index,
  tripType,
  expandedFlights,
  toggleFlightDetails,
  setSelectedFlightForPrice,
  setShowPriceModal,
  verifyFlightPrice,
  fetchSSROptions,
  handleAuthCheck,
  statusRefdId,
  formatApiDateTime,
  minutesToHm,
  getSegments,
  getStopsText,
  getTotalDurationMinutes,
  isExpanded,
  setIsExpanded,
  expandedOneWayDetails,
  setExpandedOneWayDetails,
  bookWithSSR,
}) => {
  const { currency } = useCurrency();
  const [fareOptions, setFareOptions] = useState(null);
  const [loadingFares, setLoadingFares] = useState(false);
  const [selectedFare, setSelectedFare] = useState(null);
  const [showFares, setShowFares] = useState(false);
  const [showSSRInfo, setShowSSRInfo] = useState(false);

  const resultData = flight?.Flights?.Onward || flight?.data?.result || flight;

  const getSegmentsFunc =
    getSegments ||
    ((leg) => {
      if (!leg) return [];
      if (Array.isArray(leg)) return leg.filter((s) => s && s.flightNo);
      if (typeof leg === "object") {
        const values = Object.values(leg);
        return values.filter((s) => s && s.flightNo);
      }
      return [];
    });

  const flightsData = resultData?.Flights || flight?.Flights;
  const segments = getSegmentsFunc(flightsData?.Onward);
  const firstSegment = segments?.[0] || {};
  const lastSegment = segments?.[segments.length - 1] || {};

  const airlineName =
    firstSegment?.airName || flightsData?.AirlineName || "Airline";
  const flightNo = firstSegment?.flightNo || "";

  const fareData = resultData?.Fare || flight?.Fare;
  const basePrice = parseInt(fareData?.total?.total || 0);
  
  const fareTypeInd = fareData?.fareTypeInd;
  const fareTypeDesc = fareData?.fareTypeDesc || "";
  const popMsg = fareData?.popMsg || "";
  const refundType = fareData?.refundType || "P";
  const validation = fareData?.validation || {};
  
  const depTerminal = firstSegment?.depTer || flight?.depTer || "";
  const arrTerminal = lastSegment?.arrTer || flight?.arrTer || "";
  
  const operatingAirline = firstSegment?.airNameOp || flight?.airNameOp;
  const operatingAirlineCode = firstSegment?.airCodeOp || flight?.airCodeOp;
  const isOperatingDifferent = operatingAirline && operatingAirline !== airlineName;
  
  const hasFreeMeal = validation?.freeMeal === "1" || validation?.freeMeal === 1;
  
  const getFareTypeBadge = () => {
    if (fareTypeDesc.toLowerCase().includes("student")) return { text: "Student", color: "bg-green-100 text-green-800" };
    if (fareTypeDesc.toLowerCase().includes("senior")) return { text: "Senior Citizen", color: "bg-purple-100 text-purple-800" };
    if (fareTypeDesc.toLowerCase().includes("defence")) return { text: "Defence", color: "bg-blue-100 text-blue-800" };
    if (fareTypeDesc.toLowerCase().includes("sme")) return { text: "SME", color: "bg-yellow-100 text-yellow-800" };
    if (fareTypeDesc.toLowerCase().includes("promo") || fareTypeDesc.toLowerCase().includes("offer")) return { text: "Promo", color: "bg-orange-100 text-orange-800" };
    return null;
  };
  
  const fareBadge = getFareTypeBadge();

  const handleViewPrices = async () => {
    if (showFares) {
      setShowFares(false);
      setShowSSRInfo(false);
      return;
    }

    setLoadingFares(true);
    try {
      const flightId = firstSegment?.flightID;

      if (!flightId || !statusRefdId) {
        throw new Error("Missing flight ID or reference ID");
      }

      const apiFares = await verifyFlightPrice(flightId, statusRefdId, false);
      
      console.log("🔍 API Fares received from verifyFlightPrice:", apiFares);
      console.log("🔍 API Fares count:", apiFares?.length);
      console.log("🔍 API Fares is array:", Array.isArray(apiFares));

      if (!apiFares || !Array.isArray(apiFares) || apiFares.length === 0) {
        console.error("❌ No fare options received from API");
        throw new Error("No fare options received from API");
      }

      const processedFares = apiFares.map((fare, index) => {
        // V2: SSR will be fetched when user selects this fare, not here
        // So we don't need to process SSR info at this stage
        
        // Parse baggage from API response (Fare.bagCkin and Fare.bagCbin)
        // Format: "15 KG||", "15 Kg (01 Piece only)||", "20 Kg (01 Piece only)||", etc.
        const bagCkin = fare.bagCkin || fare.Fare?.bagCkin || flightsData?.bagCkin || "";
        const bagCbin = fare.bagCbin || fare.Fare?.bagCbin || flightsData?.bagCbin || "";
        
        // Remove "||" separator and clean up
        const checkinBaggage = bagCkin.replace(/\|\|/g, "").trim();
        const cabinBaggage = bagCbin.replace(/\|\|/g, "").trim();
        
        // Extract piece count if available (e.g., "15 Kg (01 Piece only)")
        const pieceMatch = checkinBaggage.match(/\((\d+)\s*Piece/i);
        const pieceCount = pieceMatch ? pieceMatch[1] : null;
        const checkinWeight = checkinBaggage.replace(/\s*\(.*?\)/i, "").trim();

        return {
          id: `fare-${index}`,
          name: fare.fareName || fare.fareTypeDesc || `Fare ${index + 1}`,
          price: parseInt(fare.price || basePrice),
          base: parseInt(fare.base || 0),
          tax: parseInt(fare.tax || 0),
          refundable: fare.refundable || (fare.refundType === "P" ? "Partially Refundable" : "Non-Refundable"),
          baggage: checkinBaggage,
          baggageCheckin: checkinWeight,
          baggageCabin: cabinBaggage,
          baggagePieceCount: pieceCount,
          dateChangeFee: fare.dateChangeFee || "Contact Airlines",
          meals: fare.meals === "available" ? "Available" : "Paid",
          seats: fare.seats === "chargeable" ? "Paid" : "Free",
          seatsAvailable: fare.seats || fare.Fare?.seats || "9",
          seatType: fare.seatType || fare.Fare?.fareClass || "SM",
          color: "blue",
          type: "oneway",
          flightData: flight,
          // V2: Store fare-specific flightID and refID for SSR fetch
          flightID: fare.flightID, // From postFareDetails response
          refID: fare.refID, // From postFareDetails Status.refID
          // V2: Store fare type info
          fareTypeInd: fare.fareTypeInd,
          fareTypeDesc: fare.fareTypeDesc,
          popMsg: fare.popMsg,
          // SSR will be fetched when user selects this fare
          ssrInfo: null,
          baggageOptions: [],
          mealOptions: [],
          originalFare: fare,
          index: index,
        };
      });

      console.log("✅ Processed fares count:", processedFares.length);
      console.log("✅ Processed fares:", processedFares);
      
      if (processedFares.length === 0) {
        console.error("❌ No processed fares available");
        throw new Error("No fare options available");
      }
      
      // Set fare options and show them
      setFareOptions(processedFares);
      console.log("🔥 STATE UPDATE: setFareOptions called with", processedFares.length, "fares");
      
      setShowFares(true);
      console.log("🔥 STATE UPDATE: setShowFares(true) called");
      
      // Clear any previously selected fare when showing new fare options
      setSelectedFare(null);
      console.log("🔥 STATE UPDATE: setSelectedFare(null) called");
      
      // Force a re-render check
      setTimeout(() => {
        console.log("🔥 TIMEOUT CHECK: showFares should be true, fareOptions should have", processedFares.length, "items");
      }, 100);
    } catch (error) {
      console.error("Error fetching fare options:", error);
      // Parse baggage from flight data
      const defaultBagCkin = flightsData?.bagCkin || fareData?.bagCkin || "";
      const defaultBagCbin = flightsData?.bagCbin || fareData?.bagCbin || "";
      const defaultCheckin = defaultBagCkin.replace(/\|\|/g, "").trim() || "15 KG";
      const defaultCabin = defaultBagCbin.replace(/\|\|/g, "").trim() || "7 KG";
      const defaultPieceMatch = defaultCheckin.match(/\((\d+)\s*Piece/i);
      const defaultPieceCount = defaultPieceMatch ? defaultPieceMatch[1] : null;
      const defaultCheckinWeight = defaultCheckin.replace(/\s*\(.*?\)/i, "").trim();

      const defaultFare = {
        id: "oneway-default",
        name: "Standard Fare",
        price: basePrice,
        base: basePrice,
        tax: 0,
        refundable: "Partially Refundable*",
        baggage: defaultCheckin,
        baggageCheckin: defaultCheckinWeight,
        baggageCabin: defaultCabin,
        baggagePieceCount: defaultPieceCount,
        dateChangeFee: "Contact Airlines",
        meals: "Paid",
        seats: "Paid",
        seatsAvailable: fareData?.seats || "9",
        seatType: fareData?.fareClass || "SM",
        color: "blue",
        type: "oneway",
        flightData: flight,
        ssrInfo: null,
        baggageOptions: [],
        mealOptions: [],
        index: 0,
      };

      setFareOptions([defaultFare]);
      setSelectedFare(defaultFare);
      setShowFares(true);
    } finally {
      setLoadingFares(false);
    }
  };

  const handleSelectFare = async (fare) => {
    // V2 Flow: When user selects a fare from postFareDetails:
    // 1. Call postPriceVerify to verify price and get SSR options
    // 2. Show price difference if any (fareDiff)
    // 3. Display SSR options as add-ons
    
    if (fare.flightID && fare.refID && fetchSSROptions) {
      try {
        console.log("🔍 User selected fare:", fare.fareName, "Price:", fare.price);
        console.log("🔍 Calling postPriceVerify with flightID:", fare.flightID, "refID:", fare.refID);
        
        const ssrData = await fetchSSROptions(fare.flightID, fare.refID, false);
        
        // Attach SSR options to the selected fare
        const ssrOnward = ssrData.ssrInfo?.Onward || ssrData.ssrInfo?.onward || {};
        const baggageOptions = ssrOnward.Bagg || ssrOnward.bagg || [];
        const mealOptions = ssrOnward.Meal || ssrOnward.meal || [];
        
        console.log("✅ SSR Options received:");
        console.log("  - Baggage options:", baggageOptions.length);
        console.log("  - Meal options:", mealOptions.length);
        console.log("  - Price difference:", ssrData.priceDifference);
        console.log("  - Verified price:", ssrData.verifiedPrice);
        
        // V2: Calculate final price (postFareDetails price + fare difference from postPriceVerify)
        // According to V2 docs: Client should check fare difference between search and price response
        const basePrice = fare.price || 0;
        const priceDifference = ssrData.priceDifference || 0;
        const finalPrice = basePrice + priceDifference; // Add fare difference if any
        
        console.log("💰 Price Calculation:");
        console.log("  - Base price (from postFareDetails):", basePrice);
        console.log("  - Price difference (from postPriceVerify):", priceDifference);
        console.log("  - Final price:", finalPrice);
        
        const fareWithSSR = {
          ...fare,
          // V2: Use verified price if available, otherwise use base price + difference
          price: ssrData.verifiedPrice || finalPrice,
          basePrice: basePrice, // Store original price from postFareDetails
          priceDifference: priceDifference, // Store fare difference
          ssrInfo: ssrData.ssrInfo,
          baggageOptions: baggageOptions,
          mealOptions: mealOptions,
          priceCheckData: ssrData.validationRequirements,
          doc_mandatory: ssrData.validationRequirements.doc_mandatory,
          pan_mandatory: ssrData.validationRequirements.pan_mandatory,
          document_type: ssrData.validationRequirements.document_type,
          remarks: ssrData.remarks,
          genRemarks: ssrData.genRemarks,
          fNameFormat: ssrData.fNameFormat,
          lNameFormat: ssrData.lNameFormat,
        };
        
        setSelectedFare(fareWithSSR);
        
        // Always show SSR section if options are available
        if (baggageOptions.length > 0 || mealOptions.length > 0) {
          setShowSSRInfo(true);
          console.log("✅ SSR add-ons will be displayed:", {
            baggage: baggageOptions.length,
            meals: mealOptions.length
          });
        } else {
          console.warn("⚠️ No SSR options available for this fare");
          setShowSSRInfo(false);
        }
      } catch (error) {
        console.error("❌ Error fetching SSR options:", error);
        // Still set the fare even if SSR fetch fails
        setSelectedFare(fare);
        setShowSSRInfo(false);
      }
    } else {
      console.warn("⚠️ Missing flightID or refID, cannot fetch SSR options");
      setSelectedFare(fare);
      setShowSSRInfo(false);
    }
    
    setShowFares(false);
  };

  const handleOneWayBookNow = () => {
    if (!selectedFare) {
      alert("Please select a fare to proceed with booking");
      return;
    }

    const bookingData = {
      isRoundTrip: false,
      flight: flight,
      selectedFare: selectedFare,
      totalPrice: selectedFare.totalPrice || selectedFare.price,
      flightID: firstSegment?.flightID || selectedFare.flightID,
      refID: statusRefdId || selectedFare.refID,
      flightType: "oneway",
      airlineName: airlineName,
      flightNumber: flightNo,
    };

    // V2: Check if user selected any SSR add-ons (baggage or meal)
    const hasSelectedSSR = selectedFare.selectedBaggage || selectedFare.selectedMeal;
    
    if (hasSelectedSSR) {
      // Build SSR info structure for V2 API
      const ssrInfo = {
        Onward: {
          Bagg: selectedFare.selectedBaggage ? [selectedFare.selectedBaggage] : [],
          Meal: selectedFare.selectedMeal ? [selectedFare.selectedMeal] : [],
        },
      };

      const ssrBookingData = {
        ...bookingData,
        ssrInfo: ssrInfo,
        selectedBaggage: selectedFare.selectedBaggage,
        selectedMeal: selectedFare.selectedMeal,
        baggageOptions: selectedFare.baggageOptions || [],
        mealOptions: selectedFare.mealOptions || [],
        hasSSR: true,
        // V2: Validation requirements
        doc_mandatory: selectedFare.doc_mandatory || 0,
        pan_mandatory: selectedFare.pan_mandatory || 0,
        document_type: selectedFare.document_type || 0,
      };

      if (bookWithSSR) {
        bookWithSSR(ssrBookingData);
      } else if (handleAuthCheck) {
        handleAuthCheck(ssrBookingData);
      } else {
        console.error("No booking function available for SSR booking");
        alert("Unable to process booking with add-ons. Please try again.");
      }
    } else {
      const regularBookingData = {
        ...bookingData,
        hasSSR: false,
        ssrInfo: null,
        baggageOptions: [],
        mealOptions: [],
        // V2: Validation requirements
        doc_mandatory: selectedFare.doc_mandatory || 0,
        pan_mandatory: selectedFare.pan_mandatory || 0,
        document_type: selectedFare.document_type || 0,
      };

      if (handleAuthCheck) {
        handleAuthCheck(regularBookingData);
      } else {
        console.error("No booking function available");
        alert("Unable to process booking. Please try again.");
      }
    }
  };

  const flightDuration = minutesToHm
    ? minutesToHm(calculateTotalDuration(flightsData?.Onward))
    : "--h --m";

  const stopsText = calculateStopsText(flightsData?.Onward);

  const departureTime =
    formatApiDateTime && firstSegment?.depDate
      ? formatApiDateTime(firstSegment.depDate)?.split(",")[1]?.trim() || "--:--"
      : "--:--";
  const arrivalTime =
    formatApiDateTime && lastSegment?.arrDate
      ? formatApiDateTime(lastSegment.arrDate)?.split(",")[1]?.trim() || "--:--"
      : "--:--";


  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-3 hover:shadow-md transition-all duration-200 max-w-5xl mx-auto"
    >
      {/* Compact Header */}
      <div className="bg-white px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AirlineLogo 
              airlineCode={firstSegment?.airCode || ""} 
              airlineName={airlineName}
              className="w-10 h-10"
            />
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-sm font-bold text-gray-900">{airlineName}</h3>
                {fareBadge && (
                  <span className={`px-1.5 py-0.5 text-xs font-semibold rounded ${fareBadge.color}`}>
                    {fareBadge.text}
                  </span>
                )}
                {hasFreeMeal && (
                  <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-800">
                    Free Meal
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">{flightNo}</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-lg font-bold text-blue-600">
              {formatPriceSync(selectedFare ? selectedFare.price : basePrice, currency)}
            </div>
            {popMsg && (
              <span className="text-xs text-orange-600 font-medium">Sale</span>
            )}
          </div>
        </div>
      </div>

      {/* Flight Route - Compact */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="text-center flex-1">
            <div className="text-base font-bold text-gray-900">
              {firstSegment?.depCode || "---"}
            </div>
            <div className="text-sm font-semibold text-gray-700">{departureTime}</div>
            <div className="text-xs text-gray-500">
              {formatApiDateTime && firstSegment?.depDate
                ? formatApiDateTime(firstSegment.depDate)?.split(",")[0]
                : "26 Jan"}
            </div>
          </div>

          <div className="flex-1 px-4">
            <div className="relative">
              <div className="flex items-center">
                <div className="flex-1 border-t border-dashed border-gray-400"></div>
                <div className="mx-2 relative">
                  <FaPlane className="text-blue-500 text-xs" />
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 text-xs text-gray-600 whitespace-nowrap">
                    {flightDuration}
                  </div>
                </div>
                <div className="flex-1 border-t border-dashed border-gray-400"></div>
              </div>
              <div className="text-center text-xs text-gray-500 mt-1">
                {stopsText}
              </div>
            </div>
          </div>

          <div className="text-center flex-1">
            <div className="text-base font-bold text-gray-900">
              {lastSegment?.arrCode || "---"}
            </div>
            <div className="text-sm font-semibold text-gray-700">{arrivalTime}</div>
            <div className="text-xs text-gray-500">
              {formatApiDateTime && lastSegment?.arrDate
                ? formatApiDateTime(lastSegment.arrDate)?.split(",")[0]
                : "26 Jan"}
            </div>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div className="px-4 py-2 bg-white border-b border-gray-200 flex items-center justify-between">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center"
        >
          Flight Details
          {isExpanded ? (
            <FaChevronUp className="ml-1" size={10} />
          ) : (
            <FaChevronDown className="ml-1" size={10} />
          )}
        </button>
        <button
          onClick={handleViewPrices}
          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
        >
          {loadingFares ? (
            <FaSpinner className="animate-spin" />
          ) : showFares ? (
            "Hide Fares"
          ) : (
            "View Fares"
          )}
        </button>
      </div>

      {/* Flight Details Expandable */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <FlightDetails
                flight={flight}
                segments={segments}
                formatApiDateTime={formatApiDateTime}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fare Options Table */}
      <AnimatePresence>
        {showFares && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="border-t border-gray-200 bg-white">
              {/* Table Header - V2: Base Fare Types */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 text-xs font-bold text-gray-800 border-b-2 border-gray-300">
                <div className="col-span-3 flex items-center">
                  <FaTag className="mr-2 text-blue-600" />
                  Base Fare Type
                </div>
                <div className="col-span-2 flex items-center">
                  <FaSuitcase className="mr-2 text-blue-600" />
                  Included Baggage
                </div>
                <div className="col-span-2 flex items-center">
                  <FaUtensils className="mr-2 text-blue-600" />
                  Included Services
                </div>
                <div className="col-span-5 flex items-center justify-between">
                  <span className="flex items-center">
                    <span className="mr-1 text-blue-600 font-semibold">₹</span>
                    Base Price / Select
                  </span>
                </div>
              </div>

              {loadingFares ? (
                <div className="text-center py-8">
                  <FaSpinner className="animate-spin text-blue-500 text-xl mx-auto" />
                  <p className="text-gray-500 mt-2 text-xs">Loading fare options...</p>
                </div>
              ) : fareOptions && fareOptions.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {fareOptions.map((fare, idx) => {
                    const isSelected = selectedFare?.id === fare.id;
                    return (
                      <div
                        key={fare.id || idx}
                        className={`grid grid-cols-12 gap-2 px-4 py-3 hover:bg-blue-50 transition-colors ${
                          isSelected ? "bg-blue-50 border-l-4 border-blue-500" : ""
                        }`}
                      >
                        {/* Fare Rules Column - V2 Enhanced */}
                        <div className="col-span-3">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-semibold text-gray-900">
                              {fare.name || fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || `Fare ${idx + 1}`}
                            </span>
                            {(fare.fareTypeDesc || fare.originalFare?.fareTypeDesc) && (
                              <span className={`px-1.5 py-0.5 text-xs rounded font-medium ${
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("sme") ? "bg-yellow-100 text-yellow-800" :
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("retail") ? "bg-blue-100 text-blue-800" :
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("special") ? "bg-purple-100 text-purple-800" :
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("flexi") ? "bg-green-100 text-green-800" :
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("hand baggage") ? "bg-orange-100 text-orange-800" :
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("instant offer") ? "bg-pink-100 text-pink-800" :
                                (fare.fareTypeDesc || fare.originalFare?.fareTypeDesc || "").toLowerCase().includes("business") ? "bg-indigo-100 text-indigo-800" :
                                "bg-gray-100 text-gray-800"
                              }`}>
                                {fare.fareTypeDesc || fare.originalFare?.fareTypeDesc}
                              </span>
                            )}
                          </div>
                          {/* V2: Show fare type index and pop message if available */}
                          {fare.fareTypeInd && (
                            <div className="text-xs text-gray-500 mb-1">
                              Type: {fare.fareTypeInd === "0" ? "Hand Baggage" : 
                                     fare.fareTypeInd === "1" ? "Instant Offer" :
                                     fare.fareTypeInd === "2" ? "Retail" :
                                     fare.fareTypeInd === "3" ? "SME" :
                                     fare.fareTypeInd === "4" ? "Flexi" :
                                     fare.fareTypeInd === "5" ? "Special C" :
                                     fare.fareTypeInd}
                            </div>
                          )}
                          {fare.popMsg && (
                            <div className="text-xs text-orange-600 mb-1 italic">
                              {fare.popMsg}
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium ${
                              fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "text-red-600" : "text-green-600"
                            }`}>
                              {fare.refundable?.includes("Non") || fare.originalFare?.refundType === "N" ? "Non" : "Partially"} Refundable
                            </span>
                          </div>
                        </div>

                        {/* Baggage Column */}
                        <div className="col-span-2">
                          <div className="text-xs text-gray-700 space-y-1">
                            <div className="font-medium">
                              {fare.baggageCheckin || fare.baggage || "15 KG"}
                              {fare.baggagePieceCount && (
                                <span className="text-gray-600 font-normal">
                                  {" "}({fare.baggagePieceCount} Piece only)
                                </span>
                              )}
                            </div>
                            {fare.baggageCabin && (
                              <div className="text-gray-600 text-xs">
                                Cabin: {fare.baggageCabin}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Meal & Seat Column */}
                        <div className="col-span-2">
                          <div className="space-y-1 text-xs">
                            <div className="text-gray-700">
                              <span className="font-medium"># Seats –</span> {fare.seatsAvailable || "9"} {fare.seatType || "SM"}
                            </div>
                            <div className={`flex items-center ${
                              fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" 
                                ? "text-green-600" 
                                : "text-gray-600"
                            }`}>
                              <FaUtensils className="mr-1" size={10} />
                              <span>Meal – {fare.meals === "Available" || fare.originalFare?.validation?.freeMeal === "1" ? "Free" : "Paid"}</span>
                            </div>
                            <div className="text-gray-600">
                              <FaChair className="mr-1 inline" size={10} />
                              Seat – {fare.seats === "Free" ? "Free" : "Paid"}
                            </div>
                          </div>
                        </div>

                        {/* Price/Actions Column */}
                        <div className="col-span-5 flex items-center justify-between">
                          <div>
                            <div className="text-lg font-bold text-gray-900">
                              {formatPriceSync(fare.price, currency)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              Base fare per person
                            </div>
                          </div>
                          <button
                            onClick={() => handleSelectFare(fare)}
                            className={`px-4 py-2 rounded text-xs font-semibold transition-all ${
                              isSelected
                                ? "bg-green-600 text-white hover:bg-green-700"
                                : "bg-blue-600 text-white hover:bg-blue-700"
                            }`}
                          >
                            {isSelected ? "Selected" : "Select Base Fare"}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No fare options available</p>
                  <p className="text-gray-400 text-xs mt-1">Please try again or contact support</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Selected Fare with SSR Add-ons (V2) - Refactored for Clear Separation */}
      {selectedFare && !showFares && (
        <div className="border-t border-gray-300 bg-gradient-to-br from-gray-50 to-white">
          <div className="px-4 py-4 space-y-4">
            {/* BASE FARE SECTION - Mandatory Ticket Price */}
            <div className="bg-white rounded-lg border-2 border-blue-500 p-4 shadow-sm">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-semibold text-gray-700">Base Fare:</span>
                    <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                      selectedFare.fareTypeDesc?.toLowerCase().includes("sme") ? "bg-yellow-100 text-yellow-800" :
                      selectedFare.fareTypeDesc?.toLowerCase().includes("flexi") ? "bg-green-100 text-green-800" :
                      selectedFare.fareTypeDesc?.toLowerCase().includes("special") ? "bg-purple-100 text-purple-800" :
                      "bg-blue-100 text-blue-800"
                    }`}>
                      {selectedFare.fareTypeDesc || selectedFare.name || "Standard Fare"}
                    </span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {formatPriceSync(selectedFare.basePrice || selectedFare.price || 0, currency)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    per person • Mandatory ticket price
                  </div>
                  {/* Helper Text */}
                  <div className="mt-2 text-xs text-gray-600 bg-blue-50 p-2 rounded border border-blue-100">
                    <FaInfoCircle className="inline mr-1 text-blue-600" />
                    Base fare includes standard baggage and seat to fly.
                  </div>
                  {/* V2: Show price difference if any */}
                  {selectedFare.priceDifference && selectedFare.priceDifference !== 0 && (
                    <div className={`text-xs mt-2 font-medium ${
                      selectedFare.priceDifference > 0 ? "text-orange-600" : "text-green-600"
                    }`}>
                      {selectedFare.priceDifference > 0 ? "+" : ""}{formatPriceSync(Math.abs(selectedFare.priceDifference), currency)} price adjustment
                    </div>
                  )}
                  {selectedFare.popMsg && (
                    <div className="text-xs text-orange-600 mt-2 italic">
                      {selectedFare.popMsg}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* SSR ADD-ONS SECTION - Optional */}
            {(selectedFare.baggageOptions?.length > 0 || selectedFare.mealOptions?.length > 0) && (
              <div className="bg-white rounded-lg border border-gray-300 p-4 shadow-sm">
                <div className="mb-3">
                  <h4 className="text-sm font-bold text-gray-900 mb-1">
                    Add-ons (Optional – Pay only if selected)
                  </h4>
                  <p className="text-xs text-gray-600">
                    Enhance your journey with optional services
                  </p>
                </div>
                
                <div className="space-y-4">
                  {/* Baggage Options - Dropdown */}
                  {selectedFare.baggageOptions?.length > 0 && (
                    <div>
                      <label htmlFor="baggage-select" className="block text-sm font-semibold text-gray-700 mb-2">
                        <FaSuitcase className="inline mr-1 text-gray-600" /> Extra Baggage
                      </label>
                      <select
                        id="baggage-select"
                        className="w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-colors"
                        value={selectedFare.selectedBaggage?.baggID || ""}
                        onChange={(e) => {
                          if (e.target.value === "") {
                            setSelectedFare({
                              ...selectedFare,
                              selectedBaggage: null,
                            });
                          } else {
                            const selectedBag = selectedFare.baggageOptions.find(
                              b => b.baggID === e.target.value
                            );
                            if (selectedBag) {
                              setSelectedFare({
                                ...selectedFare,
                                selectedBaggage: selectedBag,
                              });
                            }
                          }
                        }}
                      >
                        <option value="">No extra baggage ({formatPriceSync(0, currency)})</option>
                        {selectedFare.baggageOptions.map((bag, idx) => (
                          <option key={idx} value={bag.baggID}>
                            {bag.baggDesc} (+{formatPriceSync(parseInt(bag.baggAmt || 0), currency)})
                          </option>
                        ))}
                      </select>
                      {selectedFare.selectedBaggage && (
                        <div className="mt-2 text-xs text-green-600 font-medium">
                          ✓ Selected: {selectedFare.selectedBaggage.baggDesc} (+{formatPriceSync(parseInt(selectedFare.selectedBaggage.baggAmt || 0), currency)})
                        </div>
                      )}
                    </div>
                  )}

                  {/* Meal Options - Dropdown */}
                  {selectedFare.mealOptions?.length > 0 && (
                    <div>
                      <label htmlFor="meal-select" className="block text-sm font-semibold text-gray-700 mb-2">
                        <FaUtensils className="inline mr-1 text-gray-600" /> Meals
                      </label>
                      <select
                        id="meal-select"
                        className="w-full text-sm border-2 border-gray-300 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-colors"
                        value={selectedFare.selectedMeal?.mealID || ""}
                        onChange={(e) => {
                          if (e.target.value === "") {
                            setSelectedFare({
                              ...selectedFare,
                              selectedMeal: null,
                            });
                          } else {
                            const selectedMeal = selectedFare.mealOptions.find(
                              m => m.mealID === e.target.value
                            );
                            if (selectedMeal) {
                              setSelectedFare({
                                ...selectedFare,
                                selectedMeal: selectedMeal,
                              });
                            }
                          }
                        }}
                      >
                        <option value="">No meal ({formatPriceSync(0, currency)})</option>
                        {selectedFare.mealOptions.map((meal, idx) => (
                          <option key={idx} value={meal.mealID}>
                            {meal.mealDesc} {meal.mealRef && `(Segment ${meal.mealRef})`} (+{formatPriceSync(parseFloat(meal.mealAmt || 0), currency)})
                          </option>
                        ))}
                      </select>
                      {selectedFare.selectedMeal && (
                        <div className="mt-2 text-xs text-green-600 font-medium">
                          ✓ Selected: {selectedFare.selectedMeal.mealDesc} {selectedFare.selectedMeal.mealRef && `(Segment ${selectedFare.selectedMeal.mealRef})`} (+{formatPriceSync(parseFloat(selectedFare.selectedMeal.mealAmt || 0), currency)})
                        </div>
                      )}
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* STICKY PRICE SUMMARY - Dynamic Updates */}
            <div className="sticky bottom-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-4 shadow-lg border-t-4 border-blue-700">
              <div className="flex items-center justify-between text-white">
                <div className="flex-1">
                  <div className="text-xs text-blue-100 mb-1">Price Summary</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-semibold">Base Fare:</span>
                    <span className="text-xl font-bold">{formatPriceSync(selectedFare.basePrice || selectedFare.price || 0, currency)}</span>
                  </div>
                  {(selectedFare.selectedBaggage || selectedFare.selectedMeal) && (
                    <div className="mt-2 space-y-1">
                      {selectedFare.selectedBaggage && (
                        <div className="text-xs text-blue-100 flex justify-between">
                          <span>Extra Baggage:</span>
                          <span className="font-medium">+{formatPriceSync(parseInt(selectedFare.selectedBaggage.baggAmt || 0), currency)}</span>
                        </div>
                      )}
                      {selectedFare.selectedMeal && (
                        <div className="text-xs text-blue-100 flex justify-between">
                          <span>Meal:</span>
                          <span className="font-medium">+{formatPriceSync(parseFloat(selectedFare.selectedMeal.mealAmt || 0), currency)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="text-right ml-4 pl-4 border-l border-blue-400">
                  <div className="text-xs text-blue-100 mb-1">Final Payable</div>
                  <div className="text-2xl font-bold">
                    {formatPriceSync((
                      (selectedFare.basePrice || selectedFare.price || 0) + 
                      (selectedFare.priceDifference || 0) +
                      (selectedFare.selectedBaggage ? parseInt(selectedFare.selectedBaggage.baggAmt || 0) : 0) +
                      (selectedFare.selectedMeal ? parseFloat(selectedFare.selectedMeal.mealAmt || 0) : 0)
                    ), currency)}
                  </div>
                  <div className="text-xs text-blue-200 mt-1">per person</div>
                </div>
              </div>
            </div>

            {/* Book Now Button */}
            <button
              onClick={handleOneWayBookNow}
              className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all shadow-md hover:shadow-lg"
            >
              Proceed to Booking
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default OneWayFlightCard;
